import {useDispatch} from 'react-redux';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import type {BoUser} from '@igp/shared/contract/pam';
import {type BoUserGroupViewModel, useBoUserGroups} from '@igp/shared/features/bo-user-group';
import type {AsyncActionMeta} from '@igp/shared/redux/async-action';
import {useReduxForm} from '@igp/shared/redux/form';
import {RealtimeMessageTrigger} from '@igp/shared/redux/realtime';
import {type UseListViewEntityProps, type UseListViewEntityResult, useViewInit, type ViewType} from '@igp/shared/redux/view';
import {EntityType} from '@models';

import {boUserActions} from './actions';
import {allRoles, BoUserQueryFieldsTypeName, BoUserTypeName, BoUserViewModelKeysTypeName, BoUserViewModelTypeName} from './constants';
import type {
    BoUserQueryFields,
    BoUserResetPasswordPayload,
    BoUserServerFilterKeys,
    BoUserUnlinkAgentPayload,
    BoUserViewModel,
    BoUserViewModelKeys,
    Roles,
} from './types';
import {generatePassword} from './utils';

type UseBoUserProps = Omit<UseListViewEntityProps<BoUserServerFilterKeys, BoUserViewModelKeys>, 'defaultSorting'>;

type UseBoUserResult = Omit<UseListViewEntityResult<BoUserViewModel, BoUserViewModelKeys>, 'handleSortChange'>;

export function useBoUser({
    viewType,
    fields,
    defaultFilters,
    defaultPaging,
    syncWithUrl,
    validateFilter,
    cleanDelay,
    displayName,
    realtimeMode,
}: UseBoUserProps): UseBoUserResult {
    const mapper = useAutoMapper();
    const queryFields = fields.map(f =>
        mapper.map<BoUserViewModelKeys, BoUserQueryFields>(f, BoUserViewModelKeysTypeName, BoUserQueryFieldsTypeName)
    );

    const {
        items: serverItems,
        totalCount,
        viewEntity,
        getSearchFilter,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
    } = useViewInit<EntityType, BoUser, BoUserServerFilterKeys, BoUserQueryFields, never>({
        viewType,
        entity: {entity: EntityType.BoUser, fields: queryFields},
        cleanDelay,
        defaultFilters,
        defaultPaging,
        syncWithUrl,
        validateFilter,
        displayName,
        realtime: realtimeMode
            ? {
                  entity: EntityType.BoUser,
                  mode: realtimeMode,
                  triggers: [
                      {
                          type: RealtimeMessageTrigger.Add,
                      },
                      {
                          type: RealtimeMessageTrigger.Update,
                      },
                  ],
              }
            : null,
    });

    const items = serverItems.map(i => mapper.tryMap<BoUser, BoUserViewModel>(i, BoUserTypeName, BoUserViewModelTypeName));
    const searchFilter = getSearchFilter(i => i);

    return {
        items: items?.map(i => i.model),
        rawItems: items?.map(i => ({isValid: i.isModelValid, raw: i.rawModelString})),
        totalCount,
        searchFilter,
        filterString: viewEntity?.filter,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
    };
}

type UseBoUserActionsResult = {
    handleUpdateUserAccountStatus: (boUserId: string, isLocked: boolean) => void;
    handleUnlinkAgent: (boUserId: string, uid: string) => void;
};

export function useBoUserActions(): UseBoUserActionsResult {
    const dispatch = useDispatch();

    function handleUpdateUserAccountStatus(boUserId: string, isLocked: boolean) {
        const meta: AsyncActionMeta = {
            actionId: boUserId,
        };
        dispatch(boUserActions.updateUserAccountStatus.request({boUserId, isLocked}, meta));
    }

    function handleUnlinkAgent(boUserId: string, uid: string) {
        const meta: AsyncActionMeta = {
            actionId: boUserId,
        };
        const payload: BoUserUnlinkAgentPayload = {boUserId, uid};
        dispatch(boUserActions.unlinkAgent.request(payload, meta));
    }

    return {
        handleUpdateUserAccountStatus,
        handleUnlinkAgent,
    };
}

type UseBoUserResetPayload = BoUserResetPasswordPayload & {
    username: string;
};

type UseBoUserResetProps = {
    onSuccess?: (value: UseBoUserResetPayload) => void;
};

type UseBoUserResetResult = {
    handleResetPassword: (boUserId: string, username: string) => void;
};

export function useBoUserReset({onSuccess}: UseBoUserResetProps): UseBoUserResetResult {
    const {handleSubmit, submit, setValue} = useReduxForm<UseBoUserResetPayload, BoUserResetPasswordPayload>({
        initialModel: {boUserId: '', newPassword: '', username: ''},
        asyncAction: boUserActions.resetPassword,
        map: model => model,
        onSuccess: ({boUserId, newPassword, username}) => onSuccess({boUserId, newPassword, username}),
        mode: 'onSubmit',
    });

    function handleResetPassword(boUserId: string, username: string) {
        const newPassword = generatePassword(8);
        setValue('boUserId', boUserId);
        setValue('newPassword', newPassword);
        setValue('username', username);
        handleSubmit(submit)();
    }

    return {
        handleResetPassword,
    };
}

type UseBoRoleGroupsProps = {
    viewType: ViewType;
};

export function useBoRoleGroups({viewType}: UseBoRoleGroupsProps): BoUserGroupViewModel[] {
    const {items} = useBoUserGroups({viewType, fields: ['id', 'name']});
    const roleGroups: BoUserGroupViewModel[] = items?.filter(group => allRoles.some(role => role === group.name));
    return roleGroups ?? [];
}

type UseAvailableGroupsProps = {
    role: Roles;
    viewType: ViewType;
};

export function useAvailableGroups({role, viewType}: UseAvailableGroupsProps): BoUserGroupViewModel[] {
    const {items} = useBoUserGroups({viewType, fields: ['id', 'name']});
    const aalGroupPrefix = 'aal_';
    const aalManagersGroupPrefix = 'aal_managers_';
    const aalGroups = items?.filter(group => group.name.startsWith(aalGroupPrefix) && !group.name.startsWith(aalManagersGroupPrefix)) ?? [];
    const aalManagersGroups = items?.filter(group => group.name.startsWith(aalManagersGroupPrefix)) ?? [];

    const availableGroupsMap: Partial<Record<Roles, BoUserGroupViewModel[]>> = {
        'Agent Managers': aalManagersGroups,
        'Country-level Partners': aalManagersGroups,
        Agents: aalGroups,
    };

    return availableGroupsMap[role] ?? [];
}
